/**
 * Cuts the maze's merged hedge runs back into instance-sized pieces, for drawing only.
 *
 * `buildGrounds` merges every collinear hedge into one long box, which is exactly right
 * for physics: fewer, bigger colliders. It is wrong for *texturing*, and the reason is
 * instancing. One `Instances` set shares one material, so it shares one texture repeat —
 * and a box's UVs run 0..1 across each face however long that face is. A 36 m outer hedge
 * and a 3 m stub therefore get the same number of leaves stretched over twelve times the
 * distance, and the long walls of the maze — the ones the player sees most from the
 * apron — smear into horizontal streaks.
 *
 * Cutting each run into roughly cell-sized pieces makes the leaf scale near-constant
 * without costing a single draw call: they are still instances of one shared geometry, just
 * more of them. The pieces are pure decoration — the colliders still come from the merged
 * boxes via `collidableBoxes`, so nothing here can change where the player may walk.
 *
 * The piece length is load-bearing twice over now. The hedge geometry (`hedgeGeometry`)
 * pins its two end faces flat so that pieces butting end to end cannot part, which is only
 * a joint the eye forgives if the pieces are all about the same length — and the hedge
 * texture maps exactly one tile across a piece, so the same evenness is what keeps the
 * baked footing shadow at the same height on every hedge in the maze.
 */

import type { Box } from '../game/profile/types';

/** Metres of hedge per drawn piece. One maze cell, so the leaf scale matches the grass. */
export const HEDGE_SEGMENT = 3;

/**
 * The pieces one run is drawn as. Divided evenly rather than by taking `target`-long
 * bites and leaving a remainder: an odd short offcut at the end of every wall would put
 * the very scale jump back that this exists to remove.
 */
export function hedgeSegments(hedges: readonly Box[], target = HEDGE_SEGMENT): Box[] {
  const pieces: Box[] = [];

  for (const hedge of hedges) {
    // The longer ground axis is the run; the other is the hedge's thickness.
    const alongX = hedge.size.x >= hedge.size.z;
    const run = alongX ? hedge.size.x : hedge.size.z;
    const count = Math.max(1, Math.round(run / target));
    const length = run / count;
    const start = (alongX ? hedge.center.x : hedge.center.z) - run / 2;

    for (let piece = 0; piece < count; piece += 1) {
      const at = start + length * (piece + 0.5);
      pieces.push({
        center: {
          x: alongX ? at : hedge.center.x,
          y: hedge.center.y,
          z: alongX ? hedge.center.z : at,
        },
        size: {
          x: alongX ? length : hedge.size.x,
          y: hedge.size.y,
          z: alongX ? hedge.size.z : length,
        },
      });
    }
  }

  return pieces;
}
